import React, { useState } from 'react';
import { Monitor, Tablet, Smartphone, Maximize2, X } from 'lucide-react';

const VP_ICONS = {
  desktop: Monitor,
  tablet: Tablet,
  mobile: Smartphone,
};

/**
 * 🟥 Auditoría visual E2E — bloque que se renderiza dentro del mensaje del
 * assistant cuando la respuesta trae `metadata.audit_screenshots`. Muestra
 * las capturas del trabajo activo en Desktop/Tablet/Mobile con lightbox.
 */
export default function AuditScreenshotsBlock({
  screenshots,
  moduleName,
  content,
  apiBase,
  language = 'es',
}) {
  const [lightbox, setLightbox] = useState(null); // {url, label, width, height}
  const isEs = language !== 'en';

  if (!Array.isArray(screenshots) || screenshots.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="audit-screenshots-block">
      {content && (
        <div className="text-sm text-zinc-300 light:text-zinc-700 whitespace-pre-line">
          {/* Extraer sólo el header (todo antes del primer ###) para no
              duplicar los títulos de viewports que ya vienen abajo */}
          {String(content).split(/^###/m)[0].trim()}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {screenshots.map((shot) => {
          const Icon = VP_ICONS[shot.viewport] || Monitor;
          const fullUrl = shot.url?.startsWith('http')
            ? shot.url
            : `${apiBase}${shot.url}`;
          return (
            <div
              key={shot.viewport}
              className="rounded-lg border border-zinc-700 bg-zinc-900/50 overflow-hidden hover:border-indigo-500 transition-colors"
              data-testid={`audit-screenshot-${shot.viewport}`}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
                <div className="flex items-center gap-2 text-xs text-zinc-300">
                  <Icon className="w-3.5 h-3.5" />
                  <span className="font-medium">{shot.label}</span>
                  <span className="text-zinc-500">
                    {shot.width}×{shot.height}
                  </span>
                </div>
                <button
                  onClick={() => setLightbox({ ...shot, fullUrl })}
                  className="p-1 hover:bg-zinc-800 rounded"
                  title={isEs ? 'Ampliar' : 'Enlarge'}
                  data-testid={`audit-zoom-${shot.viewport}`}
                >
                  <Maximize2 className="w-3.5 h-3.5 text-zinc-400" />
                </button>
              </div>
              <button
                onClick={() => setLightbox({ ...shot, fullUrl })}
                className="block w-full bg-black"
              >
                <img
                  src={fullUrl}
                  alt={`${moduleName || 'trabajo'} — ${shot.label}`}
                  className="w-full h-auto object-contain cursor-zoom-in"
                  loading="lazy"
                />
              </button>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="audit-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-white"
            data-testid="audit-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-[95vw] max-h-[92vh] flex flex-col items-center gap-2">
            <img
              src={lightbox.fullUrl}
              alt={lightbox.label}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="text-xs text-zinc-300">
              {lightbox.label} · {lightbox.width}×{lightbox.height}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
